// src/pages/Customer/CustomerSignup.jsx
import React, { useState, useEffect } from "react";
import {
  LockClosedIcon,
  UserIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../store/useAuthStore";
import toast from "react-hot-toast";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { axiosInstance } from "../../lib/axiosInstance";
import OTPVerificationModal from "../../components/common/OTPVerificationModal";

// Reusable Google Icon Component
const GoogleIcon = () => (
  <svg className="w-5 h-5 mr-2" viewBox="0 0 48 48">
    <path
      fill="#EA4335"
      d="M24 9.5c3.9 0 6.7 1.6 8.3 3.1l6.3-6.3C34.9 2.7 30 .7 24 .7 14.8.7 7.2 6.3 4.4 14.3l7.3 5.7C13.3 13.1 18.2 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.3 24.5c0-1.6-.1-3.1-.4-4.6H24v8.8h12.5c-.5 2.8-2.2 5.2-4.9 6.9l7.3 5.7c4.3-3.9 6.8-9.7 6.8-16.8z"
    />
    <path
      fill="#FBBC05"
      d="M11.7 28.1c-.4-1.2-.6-2.5-.6-3.9s.2-2.7.6-3.9l-7.3-5.7C2.7 18.1 1.7 21 1.7 24.2s1 6.1 4.7 9.8l7.3-5.9z"
    />
    <path
      fill="#34A853"
      d="M24 47.3c6.5 0 11.9-2.1 15.8-5.7l-7.3-5.7c-2.1 1.4-4.9 2.3-8.5 2.3-5.9 0-10.9-3.9-12.7-9.2l-7.3 5.7C7.2 41 14.8 47.3 24 47.3z"
    />
    <path fill="none" d="M0 0h48v48H0z" />
  </svg>
);


const CustomerSignup = () => {
  const navigate = useNavigate();
  const customerSignup = useAuthStore((state) => state.customerSignup);
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === "name" && processedValue.length > 0) {
      processedValue = processedValue.charAt(0).toUpperCase() + processedValue.slice(1);
    }

    setFormData({ ...formData, [name]: processedValue });
    // Reset email verification if email changes
    if (name === "email" && emailVerified) {
      setEmailVerified(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!formData.email) {
      toast.error("Please enter your email address.");
      return;
    }

    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setOtpLoading(true);
    try {
      const response = await axiosInstance.post("/customer/send-signup-otp", {
        email: formData.email,
      });

      if (response.data.success) {
        setShowOtpModal(true);
        // Changed message as requested
        toast.success("Please check your email for the password");
      } else {
        toast.error(response.data.message || "Failed to send OTP.");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || "Failed to send OTP. Please try again.";
      toast.error(errorMsg);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerificationSuccess = () => {
    setEmailVerified(true);
    toast.success("Email verified! You can now complete your signup.");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!emailVerified) {
      toast.error("Please verify your email first.");
      return;
    }

    // Validate all required fields
    if (!formData.name.trim()) {
      toast.error("Please enter your full name.");
      return;
    }

    if (!formData.email) {
      toast.error("Please enter your email address.");
      return;
    }

    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    if (!formData.password) {
      toast.error("Please enter a password.");
      return;
    }

    if (formData.password.length < 8) {
      toast.error("Password must be at least 8 characters long.");
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post("/customer/signup", {
        name: formData.name,
        email: formData.email,
        password: formData.password,
      });

      if (response.data.success) {
        toast.success("Account created successfully!");
        navigate("/login?type=customer");
      } else {
        toast.error(response.data.message || "Signup failed.");
      }
    } catch (error) {
      console.error("Signup submission error:", error);
      toast.error(error.response?.data?.message || "An unexpected error occurred during signup.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignupClick = async () => {
    setGoogleLoading(true);
    try {
      // Removed sessionStorage.setItem('googleAuthRedirect', ...) to always redirect to /customer/projectinvite
      const response = await axiosInstance.get("/customer/google/url");
      if (response.data.success && response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error("Could not get Google signup URL.");
        setGoogleLoading(false);
      }
    } catch (error) {
      toast.error("Error initiating Google signup.");
      console.error("Google URL fetch error:", error);
      setGoogleLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center relative"
      style={{
        backgroundImage: "url('/assets/CustomerBg.jpg')",
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh',
      }}
    >
      {/* Black overlay */}
      <div className="absolute inset-0 bg-black opacity-50 z-0"></div>
      <div className="flex flex-col w-full max-w-sm p-8 border border-gray-200 bg-white/90 rounded-2xl shadow-2xl overflow-hidden items-center backdrop-blur-md z-10 relative" style={{ boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)' }}>
        <div className="flex flex-col justify-center w-full p-4 lg:p-6 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-2 tracking-tight">SIGNUP</h2>
          <form className="mt-2 w-full" onSubmit={handleSubmit}>
            {/* Name Input */}
            <div className="mb-3">
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <UserIcon className="w-5 h-5 text-black" />
                </span>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-3 py-2 border rounded-xl bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 text-sm ${loading && !formData.name.trim() ? "border-red-500" : "border-gray-300"
                    }`}
                  style={{ backgroundColor: "#f3f4f6" }}
                  placeholder="Full Name"
                  required
                  disabled={loading || googleLoading}
                />
              </div>
            </div>

            {/* Email Input with Inline Verify Button */}
            <div className="mb-3 flex gap-2">
              <div className="flex-1 relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-5 h-5 text-black"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
                    />
                  </svg>
                </span>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-3 py-2 border rounded-xl bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 text-sm ${(loading || otpLoading) && !formData.email ? "border-red-500" : "border-gray-300"
                    }`}
                  style={{ backgroundColor: "#f3f4f6" }}
                  placeholder="Email ID"
                  required
                  disabled={loading || emailVerified || googleLoading}
                />
              </div>
            </div>

            {/* Verify Email Button */}
            <div className="mb-3">
              <button
                type="button"
                onClick={handleVerifyEmail}
                disabled={emailVerified || otpLoading || !formData.email || !/\S+@\S+\.\S+/.test(formData.email) || googleLoading}
                className={`w-full px-6 py-3 text-white transition-all duration-200 flex items-center justify-center rounded-xl text-base font-medium ${emailVerified
                  ? "bg-green-500 cursor-default"
                  : otpLoading || !formData.email || !/\S+@\S+\.\S+/.test(formData.email) || googleLoading
                    ? "opacity-50 cursor-not-allowed bg-blue-400"
                    : "bg-blue-500 hover:bg-blue-600 shadow-md hover:shadow-lg"
                  }`}
              >
                {emailVerified ? (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Verified
                  </>
                ) : otpLoading ? (
                  <>
                    <AiOutlineLoading3Quarters className="animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  "Verify Email"
                )}
              </button>
            </div>

            {/* Password Input - Only shown after email verification */}
            {emailVerified && (
              <div className="mb-3">
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                    <LockClosedIcon className="w-5 h-5 text-black" />
                  </span>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-10 py-2 border rounded-xl bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 text-sm ${loading && !formData.password ? "border-red-500" : "border-gray-300"
                      }`}
                    style={{ backgroundColor: "#f3f4f6" }}
                    placeholder="Password"
                    required
                    disabled={loading || googleLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            )}

            <div className="flex justify-center">
              <button
                type="submit"
                disabled={loading || !emailVerified || googleLoading}
                className={`w-28 px-4 py-1 mt-4 text-white transition-all duration-300 flex items-center justify-center rounded-xl text-sm font-semibold shadow-sm ${loading || !emailVerified || googleLoading
                  ? "opacity-70 cursor-not-allowed bg-blue-400"
                  : "bg-blue-600 hover:bg-blue-700"
                  }`}
              >
                {loading ? (
                  <AiOutlineLoading3Quarters className="animate-spin text-lg" />
                ) : (
                  "SIGNUP"
                )}
              </button>
            </div>

            {/* Google Signup Button */}
            <div className="flex items-center my-4">
              <div className="flex-grow border-t border-gray-300"></div>
              <span className="flex-shrink mx-4 text-gray-500 text-sm">
                OR
              </span>
              <div className="flex-grow border-t border-gray-300"></div>
            </div>
            <div className="flex justify-center">
              <button
                type="button"
                onClick={handleGoogleSignupClick}
                disabled={loading || otpLoading || googleLoading}
                className={`w-full max-w-xs px-4 py-2 border flex items-center justify-center gap-2 border-slate-200 rounded-lg text-slate-700 hover:border-slate-400 hover:text-slate-900 hover:shadow transition duration-150 ${loading || otpLoading || googleLoading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
              >
                {googleLoading ? (
                  <AiOutlineLoading3Quarters className="animate-spin text-xl" />
                ) : (
                  <>
                    <GoogleIcon />
                    <span>Sign up with Google</span>
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Already have an account? Sign in */}
          <div className="mt-6 text-xs text-gray-700">
            <span>Already have an account?</span>
            <button
              onClick={() => navigate("/login?type=customer")}
              className="ml-1 font-semibold text-gray-900 hover:underline"
              style={{ color: "#555555" }}
              disabled={loading || googleLoading}
            >
              Sign in
            </button>
          </div>
        </div>
      </div>

      <OTPVerificationModal
        isOpen={showOtpModal}
        onClose={() => setShowOtpModal(false)}
        email={formData.email}
        onVerificationSuccess={handleVerificationSuccess}
      />
    </div>
  );
};

export default CustomerSignup;